import { Component, HostListener, Inject } from '@angular/core';
import {
  DwActionEventInfo,
  DwActionPreference,
  DwColumnFormatType,
  DwComponent,
  DwComponentType,
  DwDataRequest,
  DwGridActionLocation,
  DwEventService,
  DwGridColumnDefaultRendererService,
  DwGridColumnDef,
  DwGridEvent,
  DwGridEventType,
  DwGridHeightMode,
  DwContext,
  DwMdFormContextInfo,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwQueryMetaData,
  DwMetaDataFilterTileState,
  DwMetaDataActionExpressionService,
  DwRoutingService
} from '@devwareapps/devware-cap';
import { BehaviorSubject } from 'rxjs';

import { AppMetaDataItemNames } from '../../../../../meta-data/app-meta-data.service';
import { AdminManagerService } from '../../../../admin/services/admin-manager.service';
import { DwAviatorGridRendererBaseComponent } from '../dw-aviator-grid-renderer-base.component';
import {
  AviatorMetadataGridRendererConfig,
  AviatorMetadataGridRendererCardConfig
} from './aviator-metadata-grid-renderer-config.model';
import { AviatorMetadataGridRendererConfigComponent } from './config/aviator-metadata-grid-renderer-config.component';

interface AviatorMetadataGridCellViewModel {
  column: DwGridColumnDef;
  headerText: string;
  displayValue: any;
  isHtml: boolean;
  isAction: boolean;
  isActionButtonsColumn: boolean;
  actionLabel: string;
  actionPreferences: DwActionPreference<any>[];
  actionContext: any;
}

interface AviatorMetadataGridRowViewModel {
  row: any;
  rowSubject: BehaviorSubject<any>;
  templateContext: DwContext;
  rowKey: any;
  cardImageUrl: string;
  cardTitle: string;
  showTitleAvatar: boolean;
  cells: AviatorMetadataGridCellViewModel[];
}

@DwComponent<AviatorMetadataGridRendererConfig>({
  key: 'aviator-metadata-grid-renderer',
  name: 'Aviator Metadata Grid Renderer',
  isGlobal: true,
  parentItemName: AppMetaDataItemNames.DwUser,
  componentType: DwComponentType.gridRenderer,
  configComponent: AviatorMetadataGridRendererConfigComponent,
  configData: {
    displayMode: 'table',
    emptyTitle: 'No items found',
    emptyDetail: 'Try adjusting your search or filter criteria.',
    emptyIconClass: 'fa fa-search',
    card: {
      useTitleAvatar: false
    },
    cardColumnsDesktop: 3,
    cardColumnsTablet: 2,
    cardColumnsMobile: 1,
    cardGapPx: 16
  }
})
@Component({
  selector: 'app-aviator-metadata-grid-renderer',
  templateUrl: './aviator-metadata-grid-renderer.component.html',
  styleUrls: ['./aviator-metadata-grid-renderer.component.scss']
})
export class AviatorMetadataGridRendererComponent extends DwAviatorGridRendererBaseComponent<any> {
  readonly defaultHeaderHeight = 42;
  readonly defaultRowHeight = 52;
  readonly mobileBreakpoint = 768;

  rendererConfig: AviatorMetadataGridRendererConfig = {} as AviatorMetadataGridRendererConfig;
  cardConfig: AviatorMetadataGridRendererCardConfig = {};
  emptyTitle = 'No items found';
  emptyDetail = 'Try adjusting your search or filter criteria.';
  emptyIconClass = 'fa fa-search';
  visibleColumns: DwGridColumnDef[] = [];
  selectedViewQueryMetaData: DwQueryMetaData | null = null;
  templateItemName = '';
  cardGridStyles: { [key: string]: string } = {};
  isCardMode = false;
  useTitleAvatar = false;
  resolvedTitleField = '';
  resolvedTitleTemplate = '';
  resolvedDetailTemplate = '';
  resolvedMetaTemplate = '';
  useColumnFallbackCards = false;
  surfaceStyles: { [key: string]: string } = {};
  scrollSurfaceStyles: { [key: string]: string } = {};
  rowStyles: { [key: string]: string } = {};
  headerStyles: { [key: string]: string } = {};
  rowViewModels: AviatorMetadataGridRowViewModel[] = [];
  useInternalScroll = false;
  showEmptyState = false;
  showErrorState = false;
  showLoadingState = false;
  showCardsSurface = false;
  showTableSurface = false;

  currentSortField: string | null = null;
  currentSortAscending = true;

  private rowSubjects = new WeakMap<object, BehaviorSubject<any>>();
  private columnRendererMap = new Map<string, (params: any) => any>();

  constructor(
    dwRoutingService: DwRoutingService,
    adminManagerService: AdminManagerService,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwEventService: DwEventService,
    private defaultColumnRendererService: DwGridColumnDefaultRendererService,
    private mdActionExpressionService: DwMetaDataActionExpressionService
  ) {
    super(dwRoutingService, adminManagerService, dwMetaDataService, dwEventService);
  }

  setupRenderer(): void {
    this.rebuildLayoutState();
    this.applyConfiguredSort();
    this.rebuildRowViewModels();
  }

  protected onGridConfigUpdated(): void {
    this.rebuildLayoutState();
    this.applyConfiguredSort();
    this.rebuildRowViewModels();
  }

  protected filterTileStateChange(filterTileState: DwMetaDataFilterTileState): void {
  }

  customFiltersChanged(): void {
  }

  protected afterLoad(items: any[]): void {
    this.rebuildRowViewModels(items);
  }

  protected beforeLoad(request: DwDataRequest, forceRefresh: boolean): void {
    void request;
    void forceRefresh;
    this.refreshDisplayState();
  }

  protected onLoadError(): void {
    this.rowViewModels = [];
    this.refreshDisplayState();
  }

  protected clearRendererState(): void {
    super.clearRendererState();
    this.rowViewModels = [];
    this.refreshDisplayState();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    const previousIsCardMode = this.isCardMode;
    this.updateResponsiveMode();

    if (previousIsCardMode !== this.isCardMode) {
      this.rebuildRowViewModels();
    } else {
      this.refreshDisplayState();
    }
  }

  toggleSort(column: DwGridColumnDef): void {
    if (!this.isColumnSortable(column)) {
      return;
    }

    const fieldName = this.getSortField(column);
    if (!fieldName) {
      return;
    }

    if (this.currentSortField === fieldName) {
      this.currentSortAscending = !this.currentSortAscending;
    } else {
      this.currentSortField = fieldName;
      this.currentSortAscending = column.sort !== 'desc';
    }

    this.setSorts([{
      fieldName: this.currentSortField,
      ascending: this.currentSortAscending
    } as any]);
  }

  isSorted(column: DwGridColumnDef, ascending: boolean): boolean {
    return this.currentSortField === this.getSortField(column) && this.currentSortAscending === ascending;
  }

  getCellValue(row: any, column: DwGridColumnDef): any {
    if (!column) {
      return '';
    }

    const renderer = this.getColumnRenderer(column);
    if (!renderer) {
      return this.getRawValue(row, column);
    }

    const value = renderer({
      data: row,
      colDef: column,
      getValue: () => this.getRawValue(row, column)
    });

    if (value === null || value === undefined || value === '') {
      return '—';
    }

    return value;
  }

  getCardFieldValue(row: any, fieldName?: string): string {
    if (!row || !fieldName) {
      return '';
    }

    const attr = this.selectedViewQueryMetaData?.Attributes?.find(a =>
      a.AttributeName === fieldName || a.DisplayAttributeName === fieldName);

    if (attr?.DisplayAttributeName && row[attr.DisplayAttributeName] !== undefined) {
      return row[attr.DisplayAttributeName];
    }

    if (row[fieldName] !== undefined) {
      return row[fieldName];
    }

    return '';
  }

  getRowSubject(row: any): BehaviorSubject<any> {
    if (!row || typeof row !== 'object') {
      return new BehaviorSubject<any>(row);
    }

    let subject = this.rowSubjects.get(row);
    if (!subject) {
      subject = new BehaviorSubject<any>(row);
      this.rowSubjects.set(row, subject);
    } else {
      subject.next(row);
    }

    return subject;
  }

  getRowTemplateContext(rowSubject: BehaviorSubject<any>): DwContext {
    return {
      [DwMdFormContextInfo.DATA_CONTEXT_NAME]: rowSubject
    };
  }

  onRowClick(rowVm: AviatorMetadataGridRowViewModel, event?: Event): void {
    this.setSelectedItems([rowVm.row]);
    this.onGridEvent({
      eventType: DwGridEventType.rowSelectionChange,
      eventData: this.selectedItems,
      dwBrowserEvent: null
    } as DwGridEvent);
    this.onGridEvent({
      eventType: DwGridEventType.gridRowClick,
      eventData: rowVm.row,
      browserEvent: event,
      dwBrowserEvent: null
    } as DwGridEvent);
  }

  onRowDoubleClick(rowVm: AviatorMetadataGridRowViewModel, event?: Event): void {
    this.onGridEvent({
      eventType: DwGridEventType.gridRowDoubleClick,
      eventData: rowVm.row,
      browserEvent: event,
      dwBrowserEvent: null
    } as DwGridEvent);
  }

  onRowKeydown(event: KeyboardEvent, rowVm: AviatorMetadataGridRowViewModel): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.onRowClick(rowVm, event);
  }

  onColumnAction(cell: AviatorMetadataGridCellViewModel, rowVm: AviatorMetadataGridRowViewModel, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    this.onGridEvent({
      eventType: DwGridEventType.rowActionButton,
      eventData: rowVm.row,
      eventColumn: cell.column,
      actionPreference: cell.column.actionPreference,
      browserEvent: event,
      dwBrowserEvent: null
    } as DwGridEvent);
  }

  onRowActionBarClick(actionEvent: DwActionEventInfo, rowVm: AviatorMetadataGridRowViewModel): void {
    if (!actionEvent?.actionPreference) {
      return;
    }

    this.onGridEvent({
      eventType: DwGridEventType.rowActionButton,
      eventData: rowVm.row,
      actionPreference: actionEvent.actionPreference,
      browserEvent: null,
      dwBrowserEvent: actionEvent.dwBrowserEvent
    } as DwGridEvent);
  }

  getHeaderText(column: DwGridColumnDef): string {
    return this.isActionButtonsColumn(column) ? '' : (column?.headerName || column?.key || column?.field || '');
  }

  isColumnSortable(column: DwGridColumnDef): boolean {
    return !!(this.gridConfig?.enableSorting && !column?.disabledSort && !this.isActionButtonsColumn(column));
  }

  trackByRow(index: number, rowVm: AviatorMetadataGridRowViewModel): any {
    return rowVm?.rowKey ?? index;
  }

  private applyConfiguredSort(): void {
    if (this.currentSortField || !this.visibleColumns.length) {
      return;
    }

    const sortedColumn = this.visibleColumns.find(column => column.sort);
    if (!sortedColumn) {
      return;
    }

    const fieldName = this.getSortField(sortedColumn);
    if (!fieldName) {
      return;
    }

    this.currentSortField = fieldName;
    this.currentSortAscending = sortedColumn.sort !== 'desc';
    this.setSorts([{
      fieldName: fieldName,
      ascending: this.currentSortAscending
    } as any]);
  }

  private getSortField(column: DwGridColumnDef): string | null {
    return column?.fullSortField || column?.field || column?.key || null;
  }

  private getColumnRenderer(column: DwGridColumnDef): ((params: any) => any) | null {
    if (!column) {
      return null;
    }

    const key = column.key || column.field || column.headerName || '';
    if (this.columnRendererMap.has(key)) {
      return this.columnRendererMap.get(key);
    }

    const renderer = this.defaultColumnRendererService.renderColumn(column);
    this.columnRendererMap.set(key, renderer);
    return renderer;
  }

  private getRawValue(row: any, column: DwGridColumnDef): any {
    if (!row || !column) {
      return null;
    }

    if (column.field && row[column.field] !== undefined) {
      return row[column.field];
    }

    if (column.key && row[column.key] !== undefined) {
      return row[column.key];
    }

    return null;
  }

  isActionColumn(column: DwGridColumnDef): boolean {
    return this.isActionButtonsColumn(column)
      || column?.columnFormatType === DwColumnFormatType.actionButton
      || !!column?.actionPreference;
  }

  private rebuildLayoutState(): void {
    this.rendererConfig = (this.gridConfig?.gridRendererConfig || {}) as AviatorMetadataGridRendererConfig;
    this.cardConfig = {
      useTitleAvatar: false,
      ...(this.rendererConfig.card || {})
    };
    this.emptyTitle = this.rendererConfig.emptyTitle || 'No items found';
    this.emptyDetail = this.rendererConfig.emptyDetail || this.rendererConfig.emptyMessage || 'Try adjusting your search or filter criteria.';
    this.emptyIconClass = this.rendererConfig.emptyIconClass || 'fa fa-search';
    this.visibleColumns = (this.gridConfig?.columnDefs || []).filter(column => !column.hide);
    this.selectedViewQueryMetaData = ((this.gridConfig as any)?.selectedView?.queryMetaData || null) as DwQueryMetaData;
    this.templateItemName = this.configContext?.itemName || '';
    this.updateResponsiveMode();
    this.resolvedTitleField = this.resolveTitleField();
    this.useTitleAvatar = !!this.cardConfig.useTitleAvatar;
    this.resolvedTitleTemplate = this.resolveTemplate(this.cardConfig.titleTemplate, this.resolvedTitleField);
    this.resolvedDetailTemplate = this.resolveTemplate(this.cardConfig.detailTemplate, this.cardConfig.detailField);
    this.resolvedMetaTemplate = this.resolveTemplate(this.cardConfig.metaTemplate, this.cardConfig.metaField);
    // ponytail: table-mode grids use visible columns as mobile card rows; add card fields only for custom card bodies.
    this.useColumnFallbackCards = !this.hasConfiguredCardBody();

    const gridStyle = this.gridConfig?.gridStyle || {};
    const gridHeightMode = gridStyle?.gridHeightMode || DwGridHeightMode.autoFitToScreen;
    const headerHeight = this.normalizeNumber(gridStyle?.headerHeight, this.defaultHeaderHeight);
    const rowHeight = this.normalizeNumber(gridStyle?.rowHeight, this.defaultRowHeight);
    const surfaceCssVariables = {
      '--amgr-header-height': `${headerHeight}px`,
      '--amgr-row-height': `${rowHeight}px`
    };

    this.cardGridStyles = {
      '--amgr-card-columns-desktop': `${this.rendererConfig.cardColumnsDesktop || 3}`,
      '--amgr-card-columns-tablet': `${this.rendererConfig.cardColumnsTablet || 2}`,
      '--amgr-card-columns-mobile': `${this.rendererConfig.cardColumnsMobile || 1}`,
      '--amgr-card-gap': `${this.rendererConfig.cardGapPx || 16}px`,
      ...surfaceCssVariables
    };

    this.surfaceStyles = {
      ...surfaceCssVariables
    };

    this.scrollSurfaceStyles = {};
    this.useInternalScroll = gridHeightMode !== DwGridHeightMode.fullScreen;

    const gridHeight = this.normalizeCssDimension(gridStyle?.gridHeight);
    switch (gridHeightMode) {
      case DwGridHeightMode.fixedHeight:
        if (gridHeight) {
          this.scrollSurfaceStyles.height = gridHeight;
          this.scrollSurfaceStyles.maxHeight = gridHeight;
        }
        break;
      case DwGridHeightMode.rowBased:
        this.scrollSurfaceStyles.height = 'auto';
        if (gridHeight) {
          this.scrollSurfaceStyles.maxHeight = gridHeight;
        }
        break;
      case DwGridHeightMode.autoFitToScreen:
        this.scrollSurfaceStyles.height = '100%';
        this.scrollSurfaceStyles.minHeight = '0';
        break;
      default:
        break;
    }

    this.rowStyles = {
      minHeight: `${rowHeight}px`
    };

    this.headerStyles = {
      minHeight: `${headerHeight}px`
    };

    this.refreshDisplayState();
  }

  private rebuildRowViewModels(items: any[] = this.items || []): void {
    // Cache all template-facing values so Angular change detection is not repeatedly
    // invoking row/column computation for every binding.
    this.rowViewModels = (items || []).map((row, index) => {
      const rowSubject = this.getRowSubject(row);

      return {
        row: row,
        rowSubject: rowSubject,
        templateContext: this.getRowTemplateContext(rowSubject),
        rowKey: row?.Id || row?.id || row?.UserId || row?.UserKey || index,
        cardImageUrl: this.cardConfig.imageUrlField ? this.getCardFieldValue(row, this.cardConfig.imageUrlField) : '',
        cardTitle: this.resolvedTitleField ? this.getCardFieldValue(row, this.resolvedTitleField) : '',
        showTitleAvatar: this.useTitleAvatar && !this.cardConfig.imageUrlField,
        cells: this.visibleColumns.map(column => {
          const displayValue = this.getCellValue(row, column);
          const isActionButtonsColumn = this.isActionButtonsColumn(column);
          return {
            column: column,
            headerText: this.getHeaderText(column),
            displayValue: displayValue,
            isHtml: this.isHtmlCellValue(displayValue),
            isAction: this.isActionColumn(column),
            isActionButtonsColumn: isActionButtonsColumn,
            actionLabel: column.actionPreference?.actionDisplay || 'Open',
            actionPreferences: isActionButtonsColumn ? this.getRowActionPreferences(row) : (column.actionPreference ? [column.actionPreference] : []),
            actionContext: this.getRowActionContext(row)
          };
        })
      };
    });

    this.refreshDisplayState();
  }

  private updateResponsiveMode(): void {
    const configuredDisplayMode = this.rendererConfig.displayMode || 'table';
    this.isCardMode = configuredDisplayMode === 'cards' || this.isMobileViewport();
  }

  private resolveTitleField(): string {
    if (this.cardConfig.titleField) {
      return this.cardConfig.titleField;
    }

    const titleColumn = this.visibleColumns.find(column => this.isEligibleTitleColumn(column));
    return titleColumn?.field || titleColumn?.key || '';
  }

  private resolveTemplate(templateText?: string, fieldName?: string): string {
    const trimmedTemplate = `${templateText || ''}`.trim();
    if (trimmedTemplate) {
      return trimmedTemplate;
    }

    return fieldName ? `[${fieldName}]` : '';
  }

  private hasConfiguredCardBody(): boolean {
    return !!(
      this.cardConfig.titleField ||
      this.cardConfig.detailField ||
      this.cardConfig.metaField ||
      `${this.cardConfig.titleTemplate || ''}`.trim() ||
      `${this.cardConfig.detailTemplate || ''}`.trim() ||
      `${this.cardConfig.metaTemplate || ''}`.trim()
    );
  }

  private isMobileViewport(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= this.mobileBreakpoint;
  }

  private isHtmlCellValue(value: any): boolean {
    if (typeof value !== 'string') {
      return false;
    }

    const trimmedValue = value.trim();
    return /^<([a-z][a-z0-9-]*)(\s|>)/i.test(trimmedValue);
  }

  private isActionButtonsColumn(column: DwGridColumnDef): boolean {
    return column?.columnRendererKey === 'dw-grid-column-action-buttons';
  }

  private getRowActionPreferences(row: any): DwActionPreference<any>[] {
    const actionPreferencesByMode = this.gridConfig?.actionPreferences || {};
    const rowActions = actionPreferencesByMode[DwGridActionLocation.rowButton] || [];

    return this.mdActionExpressionService
      .executeActionEnabledVisibleRules(rowActions, row, this.getResolvedGridContext())
      .initialResults;
  }

  private getRowActionContext(row: any): any {
    return {
      ...this.configContext,
      rowData: row
    };
  }

  private getResolvedGridContext(): any {
    return (this.gridConfig as any)?.resolvedContext || null;
  }

  private refreshDisplayState(): void {
    const hasRows = this.rowViewModels.length > 0;

    this.showErrorState = !this.isLoading && !!this.errorMessage;
    this.showLoadingState = this.isLoading && !hasRows;
    this.showEmptyState = !this.isLoading && !this.errorMessage && this.hasLoaded && !hasRows;
    this.showCardsSurface = !this.showEmptyState && !this.showErrorState && this.isCardMode && hasRows;
    this.showTableSurface = !this.showEmptyState && !this.showErrorState && !this.isCardMode && hasRows;
  }

  private isEligibleTitleColumn(column: DwGridColumnDef): boolean {
    if (!column || this.isActionColumn(column)) {
      return false;
    }

    if (column.columnFormatType === DwColumnFormatType.checkmark || column.columnFormatType === DwColumnFormatType.yesNo) {
      return false;
    }

    return !!(column.field || column.key);
  }

  private normalizeNumber(value: number | string | null | undefined, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  private normalizeCssDimension(value: string | number | null | undefined): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'number') {
      return `${value}px`;
    }

    const normalizedValue = `${value}`.trim();
    if (!normalizedValue) {
      return null;
    }

    return /^\d+$/.test(normalizedValue) ? `${normalizedValue}px` : normalizedValue;
  }
}
